<template>
  <h1 class="profile__title">Профиль пользователя</h1>
  <div class="profile__content">
    <BlockItem title="ФИО">
      {{ userData?.fio || '' }}
    </BlockItem>
    <BlockItem title="Email">
      {{ userData?.email || '' }}
    </BlockItem>
    <BlockItem title="Город">
      {{ userData?.city_name || '' }}
    </BlockItem>
    <BlockItem title="Должность">
      {{ userData?.position_name || '' }}
    </BlockItem>
    <BlockItem title="Роли">
      {{ userData?.group_names || '' }}
    </BlockItem>
    <BlockItem title="Способ входа">
      {{ authMethodLabel }}
    </BlockItem>
    <BlockItem v-if="canShowDeveloperSettings" title="База данных">
      {{ userData?.db_name || '' }}
    </BlockItem>

    <div class="profile__attorneys-table">
      <CustomTable
        :columns="attorneyColumns"
        :rowsData="attorneys"
        :loading="loading"
        title="Доверенности"
        :showSearch="true"
        :addBtn="true"
        :onAddClick="openAddModal"
        :custom-body="true"
      >
        <template v-slot:customBody="props">
          <q-tr>
            <custom-table-cell align="center" vertical-align="center">
              {{ props.row.attorney_id }}
            </custom-table-cell>
            <custom-table-cell align="left" vertical-align="center">
              <a
                v-if="props.row.attorney_url"
                @click.prevent="downloadAttorneyFile(props.row.attorney_id, props.row.attorney_name)"
                href="#"
                class="attorney-link"
              >
                {{ props.row.attorney_name }}
              </a>
              <span v-else>{{ props.row.attorney_name }}</span>
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              {{ formatDateCell(props.row.date_from) }}
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              {{ formatDateCell(props.row.date_end) }}
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              {{ formatUploadDate(props.row) }}
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              <CustomIcon 
                icon="trash" 
                title="Удалить"
                :approve-mode="true"
                :loading="deletingIds.includes(props.row.attorney_id)"
                :approve="() => deleteAttorney(props.row.attorney_id)"
              />
            </custom-table-cell>
          </q-tr>
        </template>
      </CustomTable>
    </div>
    
    <div v-if="canShowDeveloperSettings" class="profile__settings">
      <h2 class="profile__settings-title">Настройки разработчика</h2>
      <div class="profile__setting-item">
        <q-toggle
          v-model="showStoredProcedureLogs"
          @update:model-value="updateLogSettings"
          label="Показывать логи вызовов хранимых функций в консоли"
          color="primary"
        />
        <div class="profile__setting-description">
          При включении в консоль браузера будут выводиться все вызовы stored procedures (хранимых функций БД)
        </div>
      </div>

      <div class="profile__setting-actions">
        <q-btn
          color="primary"
          outline
          icon="rule"
          label="Инвалидные объекты"
          @click="openInvalidObjects"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import BlockItem from '@/components/common/BlockItem'
import CustomTable from '@/components/common/CustomTable'
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell'
import CustomIcon from '@/components/common/CustomIcon'
import { useAppStore } from '@/services/store/app'
import { storeToRefs } from 'pinia'
import { computed, ref, onMounted } from 'vue'
import { getAttorneys, uploadAttorney, deleteAttorney as deleteAttorneyAPI } from '@/views/profile/services'
import { API_URL } from '@/constants'
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar'
import instance from '@/services/api/index'
import { useRouter } from 'vue-router'
import { PAGES, AUTH_TYPE } from '@/constants'
import cookiesService from '@/services/cookiesService'

const appStore = useAppStore()
const { userData } = storeToRefs(appStore)
const { showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar()
const router = useRouter()

const canShowDeveloperSettings = computed(() => !!userData.value?.is_show_db_func)

const authMethodLabel = computed(() => {
  const authType = cookiesService.getService().getAuthType()
  return authType === AUTH_TYPE.keycloak ? 'Вход через BI Group' : 'Вход по email/паролю'
})

const showStoredProcedureLogs = ref(false)
const attorneys = ref([])
const loading = ref(false)
const deletingIds = ref([])

const attorneyColumns = [
  { name: 'attorney_id', label: 'ID', field: 'attorney_id', align: 'center' },
  { name: 'attorney_name', label: 'Название', field: 'attorney_name', align: 'left' },
  { name: 'date_from', label: 'Дата начала', field: 'date_from', align: 'center' },
  { name: 'date_end', label: 'Дата окончания', field: 'date_end', align: 'center' },
  { name: 'upload_date', label: 'Загружено', field: 'upload_date', align: 'center' },
]

// Загружаем настройку из localStorage при монтировании
onMounted(() => {
  const savedSetting = localStorage.getItem('show_sp_logs')
  showStoredProcedureLogs.value = savedSetting === 'true'
  loadAttorneys()
})

// Загрузка списка доверенностей
const loadAttorneys = async () => {
  loading.value = true
  try {
    const response = await getAttorneys()
    attorneys.value = response || []
  } catch (error) {
    showErrorSnackbar('Ошибка при загрузке доверенностей')
    console.error('Error loading attorneys:', error)
  } finally {
    loading.value = false
  }
}

// Открытие модального окна для добавления/редактирования
const openAddModal = () => {
  appStore.showModal('AddAttorney', {
    title: 'Добавить доверенность',
    data: null,
    onSubmit: handleAttorneySubmit
  })
}

// Обработка сохранения доверенности из модального окна
const handleAttorneySubmit = async (formData) => {
  const submitFormData = new FormData()
  
  if (formData.attorney_id) {
    submitFormData.append('attorney_id', formData.attorney_id)
  }
  
  submitFormData.append('attorney_name', formData.attorney_name)
  if (formData.date_from) submitFormData.append('date_from', formData.date_from)
  if (formData.date_end) submitFormData.append('date_end', formData.date_end)

  if (formData.attorney_file) {
    submitFormData.append('attorney_file', formData.attorney_file)
  }

  await uploadAttorney(submitFormData)
  await loadAttorneys()
}

// Удаление доверенности
const deleteAttorney = async (attorneyId) => {
  if (!confirm('Вы уверены, что хотите удалить эту доверенность?')) {
    return
  }

  deletingIds.value.push(attorneyId)
  try {
    await deleteAttorneyAPI(attorneyId)
    showSucceedSnackbar('Доверенность успешно удалена')
    await loadAttorneys()
  } catch (error) {
    showErrorSnackbar(error?.response?.data?.error || 'Ошибка при удалении доверенности')
    console.error('Error deleting attorney:', error)
  } finally {
    deletingIds.value = deletingIds.value.filter(id => id !== attorneyId)
  }
}

// Скачивание файла
const downloadFile = (url) => {
  if (!url) return
  
  let fullUrl
  if (url.startsWith('http')) {
    fullUrl = url
  } else {
    // Убираем /api из конца, если есть, и добавляем путь к файлу
    const baseUrl = API_URL.replace(/\/api\/?$/, '')
    fullUrl = `${baseUrl}${url}`
  }
  window.open(fullUrl, '_blank')
}

// Скачивание файла доверенности
const downloadAttorneyFile = async (attorneyId, fileName) => {
  if (!attorneyId) return
  
  try {
    // Делаем запрос через axios с авторизацией
    const response = await instance().get(`/common/attorneys/file/${attorneyId}/`, {
      responseType: 'blob'
    })
    
    // Получаем имя файла из заголовков или используем переданное имя
    const contentDisposition = response.headers['content-disposition']
    let downloadFileName = fileName || 'attorney_file'
    
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+?)"?$/i)
      if (fileNameMatch) {
        downloadFileName = fileNameMatch[1]
      }
    }
    
    // Создаем blob из ответа
    const blob = new Blob([response.data])
    
    // Создаем временную ссылку для скачивания
    const url = globalThis.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = downloadFileName
    document.body.appendChild(link)
    
    // Программно кликаем по ссылке для скачивания
    link.click()
    
    // Удаляем ссылку и освобождаем память
    link.remove()
    globalThis.URL.revokeObjectURL(url)
  } catch (error) {
    showErrorSnackbar('Ошибка при скачивании файла')
    console.error('Error downloading attorney file:', error)
  }
}

// Форматирование даты в ячейке (date_from / date_end)
const formatDateCell = (dateValue) => {
  if (!dateValue || dateValue === null || dateValue === '') return ''
  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim()
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const d = new Date(trimmed.split('T')[0])
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit' })
      }
    }
    return trimmed
  }
  if (typeof dateValue === 'number') {
    const d = dateValue < 10000000000 ? new Date(dateValue * 1000) : new Date(dateValue)
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit' })
  }
  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    return dateValue.toLocaleDateString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit' })
  }
  return String(dateValue)
}

// Форматирование даты загрузки с поддержкой различных форматов
// Принимает объект row и автоматически ищет поле с датой
const formatUploadDate = (row) => {
  if (!row || typeof row !== 'object') {
    console.warn('formatUploadDate: row не является объектом', row)
    return ''
  }
  
  // Сначала проверяем upload_date, так как это основное поле
  let dateValue = row.upload_date
  
  // Временное логирование для отладки
  if (!dateValue) {
    console.log('formatUploadDate: upload_date отсутствует, row:', row)
  }
  
  // Если upload_date нет или пустое, ищем в других полях
  if (!dateValue || dateValue === null || dateValue === '') {
    const dateFieldNames = [
      'created_at',
      'date_upload',
      'uploaded_at',
      'date_created',
      'created_date',
      'upload_date_time',
      'date',
      'date_create',
      'create_date'
    ]
    
    for (const fieldName of dateFieldNames) {
      if (row[fieldName] !== undefined && row[fieldName] !== null && row[fieldName] !== '') {
        dateValue = row[fieldName]
        break
      }
    }
  }
  
  // Если не нашли поле с датой, логируем для отладки
  if (!dateValue || dateValue === null || dateValue === '') {
    console.warn('Не найдено поле с датой загрузки. Все поля объекта:', Object.keys(row))
    console.warn('Значение upload_date:', row.upload_date, 'тип:', typeof row.upload_date)
    return ''
  }
  
  // Обработка различных форматов даты
  let date = null
  
  // Если это строка
  if (typeof dateValue === 'string') {
    // Убираем лишние пробелы
    const trimmed = dateValue.trim()
    
    // Пробуем разные форматы
    // PostgreSQL формат: "2024-01-15T10:30:00" или "2024-01-15 10:30:00" или "2024-01-15T10:30:00.123456"
    if (trimmed.includes('T') || trimmed.includes(' ')) {
      // Заменяем пробел на T и убираем миллисекунды если есть
      const normalized = trimmed.replace(' ', 'T').split('.')[0].split('+')[0].split('Z')[0]
      date = new Date(normalized)
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      // Просто дата: "2024-01-15"
      date = new Date(trimmed)
    } else {
      // Пробуем распарсить как есть
      date = new Date(trimmed)
    }
  } 
  // Если это число (timestamp)
  else if (typeof dateValue === 'number') {
    // Если это timestamp в секундах (PostgreSQL часто возвращает так)
    if (dateValue < 10000000000) {
      date = new Date(dateValue * 1000)
    } else {
      // Если это timestamp в миллисекундах
      date = new Date(dateValue)
    }
  }
  // Если это объект Date
  else if (dateValue instanceof Date) {
    date = dateValue
  }
  // Пробуем создать Date из любого другого типа
  else {
    date = new Date(dateValue)
  }
  
  // Проверяем валидность даты
  if (!date || Number.isNaN(date.getTime())) {
    console.warn('Не удалось распарсить дату:', dateValue, 'тип:', typeof dateValue)
    // Возвращаем исходное значение, если это строка, иначе пустую строку
    return typeof dateValue === 'string' ? dateValue : ''
  }
  
  // Форматируем дату
  try {
    const formatted = date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    return formatted || (typeof dateValue === 'string' ? dateValue : '')
  } catch (error) {
    console.warn('Ошибка при форматировании даты:', error, 'dateValue:', dateValue)
    return typeof dateValue === 'string' ? dateValue : ''
  }
}

// Сохраняем настройку в localStorage при изменении
const updateLogSettings = (value) => {
  localStorage.setItem('show_sp_logs', value.toString())
  console.log(`%c[Настройка] Логи хранимых функций: ${value ? 'ВКЛЮЧЕНЫ' : 'ВЫКЛЮЧЕНЫ'}`, 
    'color: #1976d2; font-weight: bold; font-size: 12px;')
}

const openInvalidObjects = () => {
  router.push(PAGES.invalidObjects)
}
</script>

<style scoped lang="scss">
.profile {
  &__title {
    font-size: 24px;
    padding: 0;
    margin: 0;
    line-height: unset;
  }

  &__content {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__settings {
    margin-top: 32px;
    padding: 20px;
    background: #f5f5f5;
    border-radius: 8px;
    border-left: 4px solid #1976d2;
  }

  &__settings-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 16px 0;
    color: #333;
  }

  &__setting-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__setting-description {
    font-size: 12px;
    color: #666;
    margin-left: 32px;
    line-height: 1.5;
  }

  &__setting-actions {
    margin-top: 16px;
    display: flex;
    justify-content: flex-start;
  }
}

.profile__attorneys-table {
  width: 100%;
  max-width: 50%;
  min-width: 50%;
  margin-top: 32px;
}

.attorney-link {
  color: #1976d2;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #1565c0;
    text-decoration: underline;
  }
}
</style>
